/**
 * Traduce al español los errores que la SPA no escribe ella misma.
 *
 * MSAL habla inglés siempre y no es configurable: un popup bloqueado sale como "Error opening
 * popup window. This can happen if you are using IE or if popups are blocked in the browser."
 * Ese texto llegaba tal cual a la pantalla del contador, porque las páginas muestran
 * `e.message`. Aquí se traducen los códigos que un usuario real puede provocar, redactados
 * como lo que tiene que hacer para salir del paso, no como lo que falló por dentro.
 *
 * Vive en su propio módulo a propósito: `autenticacion.ts` está simulado por completo en las
 * pruebas (`pruebas/configuracion.ts`), así que un ayudante metido ahí sería inalcanzable
 * desde cualquier prueba.
 */

/** Todos los errores de MSAL extienden AuthError, que expone `errorCode`. */
function codigoDeMsal(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined
  const codigo = (error as { errorCode?: unknown }).errorCode
  return typeof codigo === 'string' && codigo !== '' ? codigo : undefined
}

const porCodigo: Record<string, string> = {
  popup_window_error:
    'El navegador bloqueó la ventana de inicio de sesión. Permite las ventanas emergentes para este sitio y vuelve a intentarlo.',
  empty_window_error:
    'La ventana de inicio de sesión se cerró antes de cargar. Permite las ventanas emergentes para este sitio y vuelve a intentarlo.',
  user_cancelled: 'Cerraste la ventana de inicio de sesión antes de terminar. Vuelve a intentarlo.',
  popup_window_timeout: 'La ventana de inicio de sesión tardó demasiado en responder. Vuelve a intentarlo.',
  monitor_window_timeout: 'La ventana de inicio de sesión tardó demasiado en responder. Vuelve a intentarlo.',
  interaction_required: 'Tu sesión expiró. Vuelve a iniciar sesión.',
  login_required: 'Tu sesión expiró. Vuelve a iniciar sesión.',
  consent_required:
    'Falta autorizar el acceso de ContaQuick a tu cuenta. Vuelve a iniciar sesión y acepta los permisos.',
  no_account_error: 'No hay una sesión iniciada. Vuelve a entrar.',
  no_network_connectivity:
    'No hay conexión con el servicio de identidad. Revisa tu conexión e inténtalo de nuevo.',
}

export const MENSAJE_GENERICO_DE_AUTENTICACION =
  'No se pudo validar tu sesión. Vuelve a intentarlo y, si sigue igual, cierra sesión y entra de nuevo.'

export const MENSAJE_SIN_CONEXION =
  'No se pudo contactar al servidor. Revisa tu conexión e inténtalo de nuevo.'

export function mensajeDeErrorDeAutenticacion(error: unknown): string {
  const codigo = codigoDeMsal(error)

  if (codigo) {
    // El texto original sigue siendo lo útil para diagnosticar: va a la consola, no a la
    // pantalla. Un código que no esté en la tabla cae al mensaje genérico en vez de filtrar
    // el inglés — es la única forma de que agregar un caso nuevo no dependa de acordarse.
    console.warn(`[autenticación] ${codigo}`, error)
    return porCodigo[codigo] ?? MENSAJE_GENERICO_DE_AUTENTICACION
  }

  // Sin `errorCode` no viene de MSAL: son los Error que lanza la propia SPA, ya en español
  // (p. ej. "No hay sesión iniciada." de autenticacion.ts).
  return error instanceof Error && error.message ? error.message : MENSAJE_GENERICO_DE_AUTENTICACION
}
